import {createRouter, createWebHistory} from 'vue-router'

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/',
			component: () => import('./views/Sessions.vue'),
			props: true,
			name: 'dashboard',
			meta: {
				waitForUserData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
				requiresWorkspace: true,
			},
		},
		{
			path: '/auth/start-sso',
			component: () => import('shared/views/AuthStartLogin.vue'),
			name: 'auth-browserstack-login',
		},
		{
			path: '/auth/legacy-login',
			component: () => import('shared/views/AuthStartLogin.vue'),
			name: 'auth-legacy-login',
		},
		// DEPRECATED since v1.99.0, kept for backward-compatibility - should be removed after < v1.99.0 is no longer seen in logs
		{
			path: '/auth/extension-login',
			component: () => import('shared/views/AuthStartLogin.vue'),
			name: 'auth-extension-login',
		},
		{
			path: '/auth/post-sso',
			name: 'auth-post-login',
			component: () => import('shared/views/AuthPostLogin.vue'),
			meta: {
				requiresLogin: true,
				waitForUserData: true,
				waitForWorkspaceData: true,
			},
		},
		{
			path: '/auth/error',
			component: () => import('shared/views/AuthError.vue'),
			name: 'auth-error',
		},
		{
			path: '/auth/logout',
			name: 'auth-logout',
			component: () => import('shared/views/AuthLogout.vue'),
		},
		{
			path: '/logout',
			redirect: {name: 'auth-logout'},
		},
		{
			path: '/workspaces/create',
			component: () => import('shared/views/CreateWorkspace.vue'),
			name: 'workspaces-create',
			meta: {
				waitForUserData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
			},
		},
		{
			path: '/workspaces/join',
			component: () => import('shared/views/JoinWorkspace.vue'),
			name: 'workspaces-join',
			meta: {
				waitForUserData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
			},
		},
		{
			path: '/workspaces/settings',
			name: 'workspace-settings',
			component: () => import('./views/Settings.vue'),
			meta: {
				waitForUserData: true,
				waitForWorkspaceData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
				requiresWorkspace: true,
			},
		},
		{
			path: '/workspaces/:workspaceId?/settings',
			name: 'workspace-settings-base',
			component: () => import('./views/Settings.vue'),
			children: [
				{
					path: 'general',
					name: 'workspace-general-settings',
					component: () => import('./views/SettingsWorkspaceGeneral.vue'),
					meta: {
						forbiddenRoles: ['bug-reporter'],
					},
				},
				{
					path: 'notifications',
					name: 'workspace-notifications-settings',
					component: () => import('./views/SettingsWorkspaceNotifications.vue'),
				},
				{
					path: 'members',
					name: 'workspace-members-settings',
					component: () => import('./views/SettingsWorkspaceMembers.vue'),
					meta: {
						forbiddenRoles: ['bug-reporter'],
					},
				},
				{
					path: 'subscription-plan',
					name: 'workspace-subscription-plan-settings',
					component: () => import('./views/SettingsWorkspaceSubscriptionPlan.vue'),
					meta: {
						forbiddenRoles: ['bug-reporter'],
					},
				},
				{
					path: 'integrations',
					name: 'integrations-settings',
					component: () => import('./views/SettingsWorkspaceIntegrations.vue'),
				},
				{
					path: 'sdk',
					name: 'sdk-settings',
					component: () => import('./views/SettingsWorkspaceSdk.vue'),
					meta: {
						forbiddenRoles: ['full-member', 'bug-reporter'],
					},
				},
				{
					path: 'security',
					name: 'workspace-security-settings',
					component: () => import('./views/SettingsWorkspaceSecurity.vue'),
					meta: {
						forbiddenRoles: ['bug-reporter', 'full-member'],
					},
				},
			],
			meta: {
				waitForUserData: true,
				waitForWorkspaceData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
				requiresWorkspace: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/settings/delete',
			name: 'workspace-delete',
			props: true,
			component: () => import('./views/WorkspacesDelete.vue'),
			meta: {
				requiresLogin: true,
				waitForUserData: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/invite-team',
			name: 'workspace-invite',
			props: true,
			component: () => import('shared/views/WorkspaceInviteTeam.vue'),
			meta: {
				waitForUserData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
				requiresWorkspace: true,
			},
		},
		{
			path: '/workspaces/:workspaceId',
			component: () => import('./views/Sessions.vue'),
			props: true,
			name: 'workspace',
			meta: {
				waitForUserData: true,
				requiresLogin: true,
				requiresWorkspace: true,
				requiresSignupCompletion: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/request-access',
			name: 'workspace-request-access',
			props: true,
			component: () => import('shared/views/RequestWorkspaceAccess.vue'),
			meta: {
				waitForUserData: true,
				requiresLogin: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/invites',
			name: 'workspace-invites',
			props: true,
			component: () => import('./views/AcceptWorkspaceInvite.vue'),
			meta: {
				waitForUserData: true,
				requiresLogin: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/join',
			name: 'workspace-join',
			props: true,
			component: () => import('./views/AcceptWorkspaceInvite.vue'),
			meta: {
				waitForUserData: true,
				requiresLogin: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/extend-trial',
			name: 'workspace-extend-trial',
			props: true,
			component: () => import('./views/ExtendTrial.vue'),
			meta: {
				waitForUserData: true,
				requiresLogin: true,
				requiresWorkspace: true,
				waitForWorkspaceData: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/sessions/:sessionId',
			name: 'workspace-session',
			props: true,
			component: () => import('./views/Session.vue'),
		},
		{
			path: '/workspaces/:workspaceId/collections/:collectionId',
			component: () => import('./views/Sessions.vue'),
			props: true,
			name: 'workspace-collection',
			meta: {
				waitForUserData: true,
				waitForWorkspaceData: true,
				requiresLogin: true,
				requiresSignupCompletion: true,
				requiresWorkspace: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/collections/:collectionId/sessions/:sessionId',
			name: 'workspace-collection-session',
			props: true,
			component: () => import('./views/Session.vue'),
		},
		{
			path: '/sessions',
			redirect: '/',
		},
		{
			path: '/sessions/:sessionId',
			name: 'session',
			props: true,
			component: () => import('./views/Session.vue'),
		},
		{
			path: '/get-started',
			name: 'get-started',
			component: () => import('./views/Onboarding.vue'),
		},
		{
			path: '/complete-signup',
			name: 'complete-signup-base',
			component: () => import('shared/views/CompleteSignup.vue'),
			meta: {},
			children: [
				{
					path: 'profile',
					name: 'complete-signup-profile',
					component: () => import('shared/views/CompleteSignupUserProfile.vue'),
					meta: {
						waitForUserData: true,
						requiresLogin: true,
					},
				},
				{
					path: 'job-role',
					name: 'complete-signup-job-role',
					component: () => import('shared/views/CompleteSignupJobRole.vue'),
					meta: {
						requiresUserConsent: true,
						waitForUserData: true,
						requiresLogin: true,
					},
				},
				{
					path: 'workspace',
					name: 'complete-signup-workspace',
					component: () => import('shared/views/CompleteSignupWorkspace.vue'),
					meta: {
						requiresUserConsent: true,
						waitForUserData: true,
						requiresLogin: true,
					},
				},
				{
					path: 'get-started',
					name: 'complete-signup-extension',
					component: () => import('shared/views/CompleteSignupInstallExtension.vue'),
					meta: {
						waitForUserData: true,
						requiresLogin: true,
						requiresUserConsent: true,
						requiresExperimentData: true,
					},
				},
				// redirect page after installing extension
				{
					path: 'ready',
					name: 'complete-signup-ready',
					component: () => import('shared/views/CompleteSignupReady.vue'),
				},
			],
		},
		{
			path: '/settings/account',
			component: () => import('./views/Settings.vue'),
			props: true,
			children: [
				{
					path: '',
					redirect: '/settings/account/profile',
				},
				{
					path: 'profile',
					name: 'account-profile-settings',
					component: () => import('./views/SettingsAccountProfile.vue'),
				},
			],
			meta: {
				requiresLogin: true,
				waitForUserData: true,
				requiresUserConsent: true,
			},
		},
		{
			path: '/settings/account/delete',
			component: () => import('./views/AccountDelete.vue'),
			meta: {
				requiresLogin: true,
				waitForUserData: true,
				requiresUserConsent: true,
			},
		},
		{
			path: '/workspaces/:workspaceId/subscription-plan/cancel',
			name: 'workspace-subscription-plan-settings-cancellation',
			component: () => import('./views/SubscriptionPlanCancellation.vue'),
			meta: {
				requiresLogin: true,
				waitForUserData: true,
				requiresUserConsent: true,
			},
		},
		{
			path: '/user/:userId/delete',
			component: () => import('./views/AccountDeleteConfirmation.vue'),
			meta: {
				requiresLogin: true,
				waitForUserData: true,
				requiresUserConsent: true,
			},
		},
		{
			path: '/user/delete',
			component: () => import('./views/AccountDeleteConfirmation.vue'),
			meta: {
				requiresLogin: true,
				waitForUserData: true,
				requiresUserConsent: true,
			},
		},
		{
			path: '/account-deleted',
			component: () => import('./views/AccountDeleted.vue'),
		},
		{
			path: '/about',
			component: () => import('shared/views/About.vue'),
		},
		{
			path: '/credits',
			component: () => import('./views/Credits.vue'),
		},
		{
			path: '/oauth/authorize',
			component: () => import('./views/OAuthAuthorize.vue'),
			meta: {
				requiresLogin: true,
				requiresWorkspace: true,
				waitForUserData: true,
			},
		},
		{
			path: '/:sessionId',
			name: 'session-short',
			props: true,
			component: () => import('./views/Session.vue'),
		},
		{path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('shared/views/NotFound.vue')},
	],
})

router.onError(error => {
	if (/loading chunk .* failed\./i.test(error.message) && navigator.onLine) {
		window.location.reload()
	}
})

// If the "from" route had UTM query parameters, carry them over to the next route;
// This is to archive that the UTM query parameters are always present when completing the signup flow,
// when using signup flows that don't directly lead through the onboarding flow,
// e.g. when only completing the signup after having viewed a private session.
const utmParamKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid']
router.beforeEach((to, from, next) => {
	const utmParamsNotPresentInTargetUrl = Object.entries(from.query).filter(
		([key]) => utmParamKeys.includes(key) && !to.query[key]
	)
	if (!utmParamsNotPresentInTargetUrl.length) return next()

	// UTM parameters don't need to be carried over once the user's UTM parameters have been persisted on the profile view.
	if (from.name === 'complete-signup-profile' && to.path.includes('/complete-signup')) return next()

	return next({...to, query: {...to.query, ...Object.fromEntries(utmParamsNotPresentInTargetUrl)}})
})

export default router
